import * as Sentry from "@sentry/react";
import axios, { Axios, isAxiosError, type CreateAxiosDefaults } from "axios";
import createAuthRefresh from "axios-auth-refresh";

import { currentPlatform, platformApi } from "@/app/platform";
import { growthbook } from "@/lib/growthbook";
import { CommonTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type {
  GetUserDetailsOutputDto,
  RefreshTokenOutputDto,
  UserDetailsData,
} from "@/types/codegen";
import { isProduction, isTest } from "@/utils/constants";
import { allowPurchaseExpiresAtStorage } from "@/utils/localStorage";
import {
  accessTokenStorage,
  decode,
  refreshTokenStorage,
} from "@/utils/tokens";

export const BASE_API_URL = `${import.meta.env.VITE_BASE_API_URL}/v1`;
export const BASE_API_URL_V2 = `${import.meta.env.VITE_BASE_API_URL}/v2`;
export const BASE_API_URL_V3 = `${import.meta.env.VITE_BASE_API_URL}/v3`;

const config: CreateAxiosDefaults = {
  baseURL: BASE_API_URL,
  timeout: 60 * 1000, // 60 seconds - to match CloudFront's 60s timeout
};

export const api = axios.create(config);
export const unauthenticatedApi = axios.create(config);

const TOKEN_EXPIRY_BUFFER_MS = 30 * 1000;

let cachedUserData: UserDetailsData | null = null;

const invalidateUserDataCache = () => {
  cachedUserData = null;
};

const INVALIDATE_USER_DATA_CACHE_ENDPOINTS = [
  "/auth/super-qi/update-details",
  "/transactions/aqsati/check-eligibility",
];

const invalidateUserDataCacheInterceptor: ResponseInterceptorParams[0] = (
  response,
) => {
  if (
    response.config.method === "post" &&
    response.config.url &&
    INVALIDATE_USER_DATA_CACHE_ENDPOINTS.includes(response.config.url)
  ) {
    // We only care about the phone number; if it is not present, we should refetch user data on the next attempt to get it.
    if (!cachedUserData?.phoneNumber) {
      invalidateUserDataCache();
    }
  }
  return response;
};

const getUserData = async (
  userId: string,
): Promise<UserDetailsData | undefined> => {
  if (cachedUserData?.userId === userId) {
    return cachedUserData;
  }

  try {
    const { data } = await api.get<GetUserDetailsOutputDto>("/user");
    cachedUserData = data.data.userDetails;
    return cachedUserData;
  } catch {
    // logErrorInterceptor on api already reports non-401 errors to Sentry.
    // 401s are handled by createAuthRefresh; any that still reach here are non-critical.
    return undefined;
  }
};

const refreshTokens = async () => {
  const oldRefreshToken = refreshTokenStorage.get();
  if (!oldRefreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await unauthenticatedApi.post<RefreshTokenOutputDto>(
    "/auth/tokens/refresh",
    {
      refreshToken: oldRefreshToken,
    },
  );

  const { accessToken, refreshToken } = response.data.data;
  accessTokenStorage.set(accessToken);
  refreshTokenStorage.set(refreshToken);
  return accessToken;
};

const getTokenData = async () => {
  const existingToken = accessTokenStorage.get();

  if (existingToken) {
    try {
      const { exp } = decode(existingToken);
      // Include a buffer to avoid using a token that expires mid-request.
      // Tokens without an exp claim are treated as expired so they don't get reused indefinitely.
      const isExpired =
        exp === undefined || exp * 1000 < Date.now() + TOKEN_EXPIRY_BUFFER_MS;

      if (!isExpired) {
        // Reusing the stored token avoids an unnecessary platform sign-in round trip.
        return {
          accessToken: existingToken,
          source: "storage",
        } as const;
      }
    } catch {
      // decode already cleared storage for InvalidTokenError; the current auth
      // attempt should recover rather than surface the error to the caller.
    }

    // Try the refresh token before falling back to a full platform sign-in.
    // This preserves the session if platformApi.signIn() has a transient failure
    // (e.g. SuperQi auth-code timeout): tokens are only replaced once a new one
    // is obtained, never cleared prematurely.
    try {
      const newAccessToken = await refreshTokens();
      return { accessToken: newAccessToken, source: "storage" } as const;
    } catch {
      // Refresh failed (expired, invalid, or transient error); platformApi.signIn() is the only way to get new credentials.
    }
  }

  // No stored credential exists; the platform is the source of the initial token pair.
  const { accessToken, refreshToken } = await platformApi.signIn();

  accessTokenStorage.set(accessToken);
  refreshTokenStorage.set(refreshToken);

  return { accessToken, source: "sign_in" } as const;
};

export async function authenticate() {
  const { accessToken, source } = await getTokenData();
  const { userId } = decode(accessToken);

  Sentry.setUser({ id: userId });
  growthbook.setAttributes({ id: userId });
  // Establish user identity early so subsequent analytics events are attributed correctly.
  rudderAnalytics.identify(userId);

  // Only track USER_LOGGED_IN on a real sign-in, not on a stored-token reuse.
  if (source === "sign_in") {
    rudderAnalytics.track(CommonTrackingEvents.USER_LOGGED_IN, {
      login_method: currentPlatform,
    });
  }

  return accessToken;
}

/**
 * Enriches the analytics identity with the user's phone number.
 * Non-critical — intended to be called fire-and-forget, must not block rendering.
 */
export async function fetchUserAnalytics(accessToken: string): Promise<void> {
  try {
    const { userId } = decode(accessToken);
    const userData = await getUserData(userId);
    rudderAnalytics.identify(userId, { phone: userData?.phoneNumber });
  } catch {
    // Non-critical — failure here should never surface to the user.
  }
}

type RequestInterceptorParams = Parameters<
  Axios["interceptors"]["request"]["use"]
>;
type ResponseInterceptorParams = Parameters<
  Axios["interceptors"]["response"]["use"]
>;

const addAuthHeadersInterceptor: RequestInterceptorParams[0] = (config) => {
  const token = accessTokenStorage.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

const addCustomHeadersInterceptor: RequestInterceptorParams[0] = (config) => {
  config.headers["X-Timestamp"] = new Date().toISOString();
  return config;
};

const blockOrdersIfPurchaseNotAllowed: RequestInterceptorParams[0] = (
  config,
) => {
  if (
    config.url &&
    ["/orders", "/orders/package"].includes(config.url) &&
    config.method === "post" &&
    !isProduction &&
    !Number(allowPurchaseExpiresAtStorage.get())
  ) {
    return Promise.reject(new axios.Cancel("Purchase blocked"));
  }
  return config;
};

const onResponseSuccess: ResponseInterceptorParams[0] = (response) => response;

const refreshAuthLogic = async (failedRequest: unknown) => {
  if (!isAxiosError(failedRequest)) {
    throw failedRequest;
  }

  try {
    const newAccessToken = await refreshTokens();
    if (failedRequest.response) {
      failedRequest.response.config.headers["Authorization"] =
        `Bearer ${newAccessToken}`;
    }
  } catch {
    accessTokenStorage.remove();
    refreshTokenStorage.remove();

    // Both the original request's token and the refresh token failed
    // (expired, invalid, or transient error); re-authenticating restores
    // a valid session before the failed request is replayed.
    const newAccessToken = await authenticate();
    if (failedRequest.response) {
      failedRequest.response.config.headers["Authorization"] =
        `Bearer ${newAccessToken}`;
    }
  }
};

createAuthRefresh(api, refreshAuthLogic);

const logErrorInterceptor: ResponseInterceptorParams[1] = (error) => {
  if (axios.isCancel(error) || (isAxiosError(error) && error.status === 401)) {
    return Promise.reject(error);
  }

  if (!isProduction && !isTest) {
    console.error(
      isAxiosError(error)
        ? `API ERROR: [${error.config?.method}] ${error.config?.url} ${error.response ? error.response.status : "Unknown"}`
        : `API ERROR: ${error?.message}`,
    );
  }

  Sentry.captureException(error);

  return Promise.reject(error);
};

api.interceptors.request.use(addAuthHeadersInterceptor);
api.interceptors.request.use(addCustomHeadersInterceptor);
api.interceptors.response.use(onResponseSuccess, logErrorInterceptor);
api.interceptors.response.use(invalidateUserDataCacheInterceptor);

if (!isProduction) {
  api.interceptors.request.use(blockOrdersIfPurchaseNotAllowed);
}

unauthenticatedApi.interceptors.request.use(addCustomHeadersInterceptor);
unauthenticatedApi.interceptors.response.use(
  onResponseSuccess,
  logErrorInterceptor,
);
